import { useGetBeamstarSessionsQuery, useLazyGetBeamstarSessionQuery } from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { type BeamstarSessionDetail } from '@consigli/types';
import { saveBlob } from '@consigli/utils';
import { Download } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  Checkbox,
  DataTable,
  DEFAULT_PAGE_SIZE,
  errorToast,
  Pagination,
  Search,
  Select,
} from '@/components';

import { LoadError } from '../load-error';
import { sessionColumns } from './columns';
import { ALL_PROJECTS, inProject, type ProjectOption, projectOptions } from './project-filter';
import { filterSessions } from './session-problems';
import { buildTranscriptCsv } from './sessions-export';

// Read a few sessions at a time, so a long range does not flood the building agent.
const SESSIONS_PER_BATCH = 5;

interface SessionsTabProps {
  days: number;
  onOpenSession: (conversationId: string) => void;
}

export const SessionsTab = ({ days, onOpenSession }: SessionsTabProps) => {
  const { data, isFetching, error } = useGetBeamstarSessionsQuery({ days });
  const [search, setSearch] = useState('');
  const [onlyWithProblems, setOnlyWithProblems] = useState(false);
  const [project, setProject] = useState<ProjectOption>(ALL_PROJECTS);
  const [page, setPage] = useState(1);
  const [getSession] = useLazyGetBeamstarSessionQuery();
  const [isPreparingCsv, setIsPreparingCsv] = useState(false);

  const projects = useMemo(() => projectOptions(data ?? []), [data]);
  // A new range can drop the project on show, which then falls back to all of them.
  const shownProject = projects.find((option) => option.value === project.value) ?? ALL_PROJECTS;
  const sessions = useMemo(
    () => inProject(filterSessions(data ?? [], search, onlyWithProblems), shownProject),
    [data, search, onlyWithProblems, shownProject],
  );
  // A narrower range or filter can leave fewer pages than the one on show.
  const currentPage = Math.min(page, Math.max(1, Math.ceil(sessions.length / DEFAULT_PAGE_SIZE)));

  const downloadCsv = async () => {
    setIsPreparingCsv(true);
    try {
      const details: BeamstarSessionDetail[] = [];
      for (let start = 0; start < sessions.length; start += SESSIONS_PER_BATCH) {
        const batch = sessions.slice(start, start + SESSIONS_PER_BATCH);
        const read = await Promise.all(
          batch.map((session) => getSession({ conversationId: session.conversationId }).unwrap()),
        );
        details.push(...read);
      }
      const today = new Date().toISOString().slice(0, 10);
      saveBlob(
        `beamstar-sessions-${today}-last-${days}-days.csv`,
        new Blob([buildTranscriptCsv(details)], { type: 'text/csv;charset=utf-8;' }),
      );
    } catch {
      errorToast({ message: 'Could not read the sessions for the CSV. Try again.' });
    } finally {
      setIsPreparingCsv(false);
    }
  };

  if (error) {
    return <LoadError title="Could not load the sessions" error={error} />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-6">
        <Search
          text={search}
          placeholder="Search building or workflow"
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          className="w-[360px]"
        />
        <Select
          options={projects}
          value={shownProject}
          onValueChange={(option) => {
            setProject(option);
            setPage(1);
          }}
          getOptionLabel={(option) => option.label}
          getOptionValue={(option) => option.value}
          showSearch
        />
        <Checkbox
          id="sessions-only-with-problems"
          label="Only with problems"
          checked={onlyWithProblems}
          onCheckedChange={(checked) => {
            setOnlyWithProblems(checked);
            setPage(1);
          }}
        />
        <Button
          onClick={() => void downloadCsv()}
          disabled={isFetching || isPreparingCsv || sessions.length === 0}
          className="ml-auto cursor-pointer"
        >
          <Download className="h-4 w-4" />
          {isPreparingCsv ? 'Preparing CSV' : 'Download CSV'}
        </Button>
      </div>
      <DataTable
        columns={sessionColumns}
        data={sessions.slice(
          (currentPage - 1) * DEFAULT_PAGE_SIZE,
          currentPage * DEFAULT_PAGE_SIZE,
        )}
        isLoading={isFetching}
        onRowClick={(session) => onOpenSession(session.conversationId)}
      />
      <Pagination
        currentPage={currentPage}
        totalCount={sessions.length}
        pageSize={DEFAULT_PAGE_SIZE}
        onPageChange={setPage}
      />
    </div>
  );
};
